import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Package, Tag, Repeat } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const BulkPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/enquiries', {
        ...formData,
        enquiry_type: 'bulk',
      });
      toast.success('Enquiry submitted successfully! We\'ll contact you soon.');
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast.error('Failed to submit enquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const services = [
    {
      icon: Package,
      title: 'Bulk Manufacturing',
      desc: 'Large-scale production with consistent quality and timely delivery.',
    },
    {
      icon: Tag,
      title: 'White Label',
      desc: 'Manufacture products under your brand name with custom branding.',
    },
    {
      icon: Repeat,
      title: 'Private Label',
      desc: 'Exclusive designs manufactured solely for your business.',
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="bulk-page">
      {/* Hero */}
      <section className="section-spacing bg-brand-timberwolf" data-testid="bulk-hero">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-brand-pearl leading-none mb-6" data-testid="bulk-hero-heading">
              Bulk & Private Label Manufacturing
            </h1>
            <p className="text-base sm:text-lg text-brand-walnut max-w-3xl mx-auto leading-relaxed">
              Partner with us for large-scale apparel manufacturing. We provide bulk production, white-label, and private-label services for distributors, retailers, and e-commerce platforms.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="section-spacing" data-testid="bulk-services">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, idx) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="bg-brand-timberwolf rounded-sm border border-brand-pearl/10 p-8 text-center"
                  data-testid={`bulk-service-${idx}`}
                >
                  <div className="w-16 h-16 bg-brand-pearl rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-brand-ivory" />
                  </div>
                  <h3 className="text-2xl font-heading font-semibold text-brand-pearl mb-3">
                    {service.title}
                  </h3>
                  <p className="text-brand-walnut">{service.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-spacing bg-brand-timberwolf" data-testid="bulk-why-choose">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-brand-pearl mb-4">
              Why Partner with Rudra Apparel?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              'Minimum order quantities starting from 500 units',
              'Scalable production capacity up to 50,000 units/month',
              'Competitive pricing for bulk orders',
              'Complete customization: fabrics, colors, branding',
              'Quality assurance at every production stage',
              'Flexible payment terms for long-term partners',
            ].map((point, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                className="flex items-start space-x-3"
                data-testid={`bulk-point-${idx}`}
              >
                <div className="w-6 h-6 rounded-full bg-brand-green flex-shrink-0 flex items-center justify-center mt-0.5">
                  <span className="text-white text-xs">✓</span>
                </div>
                <p className="text-brand-walnut">{point}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section className="section-spacing" data-testid="bulk-form-section">
        <div className="container-custom max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-brand-pearl mb-4">
              Get in Touch
            </h2>
            <p className="text-brand-walnut">
              Fill out the form below and our team will get back to you within 24 hours.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            onSubmit={handleSubmit}
            className="bg-brand-timberwolf rounded-sm border border-brand-pearl/10 p-8"
            data-testid="bulk-enquiry-form"
          >
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="name">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  data-testid="bulk-form-name"
                  className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="email">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="bulk-form-email"
                    className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="phone">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    data-testid="bulk-form-phone"
                    className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="company">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  data-testid="bulk-form-company"
                  className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="message">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  data-testid="bulk-form-message"
                  rows="5"
                  placeholder="Tell us about your requirements: quantity, timeline, customization needs..."
                  className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="bulk-submit-btn"
                className="w-full flex items-center justify-center px-8 py-4 bg-brand-pearl text-brand-ivory font-medium rounded-sm hover:bg-brand-walnut transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : (
                  <>
                    <Send className="mr-2 w-5 h-5" />
                    Submit Enquiry
                  </>
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </section>
    </div>
  );
};

export default BulkPage;